import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAuth0 } from '@auth0/auth0-react';
import { useThreadLoading } from './ThreadLoadingContext';
import api from '../api';

// Define the active form type
export interface ActiveForm {
  id: string;
  assetId: string;
  componentId: string;
  componentType: string;
  getValues: () => Record<string, any>;
  setValues: (
    values: Record<string, any>,
    restoreValues?: () => Promise<void>,
    isRestore?: boolean,
  ) => void;
}

// Define the context type
interface FormCaptureContextType {
  // Core state management
  setActiveForm: (threadId: string, form: ActiveForm | null) => void;
  getActiveForm: (threadId: string) => ActiveForm | null;
  clearActiveForm: (threadId: string) => void;

  // Wrapper function that automatically generates UUID for components
  registerActiveForm: (
    threadId: string,
    assetId: string,
    componentId: string,
    componentType: string,
    getValues: () => Record<string, any>,
    setValues: (
      values: Record<string, any>,
      restoreValues?: () => Promise<void>,
      isRestore?: boolean,
    ) => void,
  ) => string;

  // Helper to get form values for a thread (returns empty object if no active form)
  getFormValues: (
    threadId: string,
  ) => { id: string; values: Record<string, any> } | null;

  // Update form values from external source (e.g., pusher notification)
  updateFormValues: (formId: string, values: Record<string, any>) => void;
}

// Create the context
const FormCaptureContext = createContext<FormCaptureContextType | undefined>(
  undefined,
);

// Provider props
interface FormCaptureProviderProps {
  children: ReactNode;
}

// Create the provider
export const FormCaptureProvider: React.FC<FormCaptureProviderProps> = ({
  children,
}) => {
  // Simple map of threadId -> ActiveForm | null
  const [activeForms, setActiveForms] = useState<
    Record<string, ActiveForm | null>
  >({});

  // Get ThreadLoadingContext for Pusher event registration
  const {
    registerCanvasFormUpdateCallback,
    unregisterCanvasFormUpdateCallback,
  } = useThreadLoading();

  // Set active form for a thread
  const setActiveFormForThread = useCallback(
    (threadId: string, form: ActiveForm | null) => {
      setActiveForms((prev) => ({
        ...prev,
        [threadId]: form,
      }));
    },
    [],
  );

  // Get active form for a thread
  const getActiveFormForThread = useCallback(
    (threadId: string): ActiveForm | null => {
      return activeForms[threadId] || null;
    },
    [activeForms],
  );

  // Get form values for a thread (helper function)
  const getFormValuesForThread = useCallback(
    (threadId: string): { id: string; values: Record<string, any> } | null => {
      const activeForm = activeForms[threadId];
      if (!activeForm) return null;

      return {
        id: activeForm.id,
        values: activeForm.getValues(),
      };
    },
    [activeForms],
  );

  // Update form values by ID (called from pusher notifications)
  const updateFormValuesById = useCallback(
    (formId: string, values: Record<string, any>) => {
      // Find the form with the matching ID and update its values
      Object.values(activeForms).forEach((form) => {
        console.log('[updateFormValuesById] form:', form, formId);
        if (form && form.id === formId) {
          form.setValues(values);
        }
      });
    },
    [activeForms],
  );

  // Get Auth0 for API calls
  const { getAccessTokenSilently } = useAuth0();

  // Handle canvas-form-update Pusher events
  const handleCanvasFormUpdate = useCallback(
    async (threadId: string, data: { formValueId: string }) => {
      console.log('FormCaptureContext: Received canvas-form-update:', data);

      try {
        // Fetch the latest form values from the backend using api library
        const token = await getAccessTokenSilently();
        const response = await api.get(`/form-values/${data.formValueId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.data.success && response.data.values) {
          console.log(
            'FormCaptureContext: Fetched latest form values:',
            response.data.values,
          );
          updateFormValuesById(data.formValueId, response.data.values);
        } else {
          console.error(
            'FormCaptureContext: Failed to fetch form values:',
            response.data.error,
          );
        }
      } catch (error: any) {
        console.error(
          'FormCaptureContext: Error fetching form values:',
          error.message || error,
        );
      }
    },
    [updateFormValuesById, getAccessTokenSilently],
  );

  // Register Pusher callback for canvas-form-update events
  useEffect(() => {
    const callbackId = 'form-capture-context';
    registerCanvasFormUpdateCallback(callbackId, handleCanvasFormUpdate);

    return () => {
      unregisterCanvasFormUpdateCallback(callbackId);
    };
  }, [
    registerCanvasFormUpdateCallback,
    unregisterCanvasFormUpdateCallback,
    handleCanvasFormUpdate,
  ]);

  // Clear active form for a thread
  const clearActiveForm = useCallback(
    (threadId: string) => {
      setActiveFormForThread(threadId, null);
    },
    [setActiveFormForThread],
  );

  // Register active form with automatic UUID generation
  const registerActiveForm = useCallback(
    (
      threadId: string,
      assetId: string,
      componentId: string,
      componentType: string,
      getValues: () => Record<string, any>,
      setValues: (
        values: Record<string, any>,
        restoreValues?: () => Promise<void>,
        isRestore?: boolean,
      ) => void,
    ): string => {
      const formId = `form-${uuidv4()}`;

      // Create restore function that fetches history and restores to previous version
      const createRestoreFunction =
        (formId: string) => async (): Promise<void> => {
          try {
            console.log('[FormCaptureContext] Restoring form:', formId);

            // Get access token for API calls
            const token = await getAccessTokenSilently();

            // Fetch form history
            const historyResponse = await api.get(
              `/form-values/${formId}/history`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              },
            );

            if (
              historyResponse.data.success &&
              historyResponse.data.history.length > 0
            ) {
              // Get the latest version from history (most recent entry)
              const latestVersion =
                historyResponse.data.history[
                  historyResponse.data.history.length - 1
                ];
              console.log(
                '[FormCaptureContext] Restoring to version:',
                latestVersion,
              );

              // Optimistically update the form values (mark as restore to avoid setting hasAiEdits)
              setValues(latestVersion.values, undefined, true);

              // Call the backend restore endpoint
              await api.post(
                `/form-values/${formId}/restore/${latestVersion.version}`,
                {},
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                },
              );

              console.log('[FormCaptureContext] Form restored successfully');
            } else {
              console.warn(
                '[FormCaptureContext] No history found for form:',
                formId,
              );
            }
          } catch (error) {
            console.error('[FormCaptureContext] Error restoring form:', error);
            // Could potentially revert the optimistic update here if needed
          }
        };

      // Create a wrapper setValues function that provides the restore callback
      const wrappedSetValues = (
        values: Record<string, any>,
        restoreValues?: () => Promise<void>,
        isRestore?: boolean,
      ) => {
        const restoreFunction = restoreValues || createRestoreFunction(formId);
        setValues(values, restoreFunction, isRestore);
      };

      const activeForm: ActiveForm = {
        id: formId,
        assetId,
        componentId,
        componentType,
        getValues,
        setValues: wrappedSetValues,
      };

      setActiveFormForThread(threadId, activeForm);
      return formId;
    },
    [setActiveFormForThread, getAccessTokenSilently],
  );

  // Context value
  const contextValue: FormCaptureContextType = {
    setActiveForm: setActiveFormForThread,
    getActiveForm: getActiveFormForThread,
    clearActiveForm,
    registerActiveForm,
    getFormValues: getFormValuesForThread,
    updateFormValues: updateFormValuesById,
  };

  return (
    <FormCaptureContext.Provider value={contextValue}>
      {children}
    </FormCaptureContext.Provider>
  );
};

// Custom hook to use the form capture context
export const useFormCapture = (): FormCaptureContextType => {
  const context = useContext(FormCaptureContext);

  if (context === undefined) {
    throw new Error('useFormCapture must be used within a FormCaptureProvider');
  }

  return context;
};

// Hook to reactively watch the active form for a specific thread
export const useActiveFormState = (threadId: string): ActiveForm | null => {
  const { getActiveForm } = useFormCapture();
  const [activeForm, setActiveForm] = React.useState<ActiveForm | null>(() =>
    getActiveForm(threadId),
  );

  React.useEffect(() => {
    // Set up an interval to check for active form changes
    // This is a simple polling approach - in a more sophisticated setup,
    // we could use a subscription pattern
    const checkActiveForm = () => {
      const currentActiveForm = getActiveForm(threadId);
      setActiveForm((prev) => {
        // Only update if the active form actually changed
        if (prev?.id !== currentActiveForm?.id) {
          return currentActiveForm;
        }
        return prev;
      });
    };

    // Check immediately
    checkActiveForm();

    // Set up polling every 100ms to detect changes
    const interval = setInterval(checkActiveForm, 100);

    return () => clearInterval(interval);
  }, [threadId, getActiveForm]);

  return activeForm;
};

// Helper hook for components to register themselves as active forms
export const useActiveForm = (
  threadId: string,
  assetId: string,
  componentId: string,
  componentType: string,
  getValues: () => Record<string, any>,
  setValues: (
    values: Record<string, any>,
    restoreValues?: () => Promise<void>,
    isRestore?: boolean,
  ) => void,
  isActive: boolean,
): string | null => {
  const { setActiveForm } = useFormCapture();
  const [currentUuid, setCurrentUuid] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isActive) {
      // Generate a UUIDv4 for this form
      const formId = `form-${uuidv4()}`;

      const activeForm: ActiveForm = {
        id: formId,
        assetId,
        componentId,
        componentType,
        getValues,
        setValues,
      };

      setActiveForm(threadId, activeForm);
      setCurrentUuid(formId);

      return () => {
        setActiveForm(threadId, null);
        setCurrentUuid(null);
      };
    } else {
      setActiveForm(threadId, null);
      setCurrentUuid(null);
    }
  }, [
    isActive,
    threadId,
    assetId,
    componentId,
    componentType,
    getValues,
    setValues,
    setActiveForm,
  ]);

  return currentUuid;
};
